import { MaterialIcon, MaterialIconName } from '@koolumbus/web/ui';
import { createTxs } from '@koolumbus/web/utils';
import React, { useMemo } from 'react';
import { PropertyData } from '../../../lib/models';

interface PropertyDetailsGridProps {
  property: PropertyData;
}

type ItemDetails = {
  label: string;
  icon: MaterialIconName;
};

const PropertyDetailsGrid: React.FC<React.PropsWithChildren<PropertyDetailsGridProps>> = ({
  property,
}) => {
  const tx = useTxs();

  const items = useMemo(() => {
    const {
      details: {
        capacity,
        squareMeters,
        bedrooms,
        restrooms,
        doubleBeds,
        singleBeds,
        doubleSofaBeds,
        singleSofaBeds,
      },
    } = property;

    const out: ItemDetails[] = [
      { icon: 'people', label: tx.guests(capacity) },
      { icon: 'straighten', label: `${squareMeters || '-'} m²` },
      { icon: 'bedroom-child', label: tx.bedrooms(bedrooms) },
      { icon: 'bathroom', label: tx.restrooms(restrooms) },
    ];

    if (doubleBeds) {
      out.push({
        icon: 'king-bed',
        label: tx.doubleBeds(doubleBeds),
      });
    }

    if (singleBeds) {
      out.push({
        icon: 'single-bed',
        label: tx.singleBeds(singleBeds),
      });
    }

    const sofas = (doubleSofaBeds ? doubleSofaBeds : 0) + (singleSofaBeds ? singleSofaBeds : 0);
    if (sofas > 0) {
      out.push({ icon: 'weekend', label: tx.sofas(sofas) });
    }

    return out;
  }, [property, tx]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-y-6 my-12">
      {items.map((item, i) => (
        <div className="flex items-center" key={i}>
          <MaterialIcon variant="outlined" icon={item.icon} className="text-xl text-gray-500" />
          <p className="ml-3 text-gray-600">{item.label}</p>
        </div>
      ))}
    </div>
  );
};

const getEnLabel = (word: string, value: number | null | undefined) => {
  return `${value ?? '-'} ${word}${(value ?? 0) > 1 ? 's' : ''}`;
};

const getItLabel = (value: number | null | undefined, singular: string, plural: string) => {
  return (value ?? 0) === 1 ? `${value ?? '-'} ${singular}` : `${value ?? '-'} ${plural}`;
};

const useTxs = createTxs({
  en: {
    guests: (value: number | null) => getEnLabel('guest', value),
    bedrooms: (value: number | null) => getEnLabel('bedroom', value),
    restrooms: (value: number | null) => getEnLabel('restroom', value),
    doubleBeds: (value: number | null) => getEnLabel('double bed', value),
    singleBeds: (value: number | null) => getEnLabel('single bed', value),
    sofas: (value: number | null) => getEnLabel('sofa', value),
  },
  it: {
    guests: (value) => getItLabel(value, 'ospite', 'ospiti'),
    bedrooms: (value) => getItLabel(value, 'camera da letto', 'camere da letto'),
    restrooms: (value) => getItLabel(value, 'bagno', 'bagni'),
    doubleBeds: (value) => getItLabel(value, 'letto doppio', 'letti doppi'),
    singleBeds: (value) => getItLabel(value, 'letto singolo', 'letti singoli'),
    sofas: (value) => getItLabel(value, 'divano', 'divani'),
  },
});

export default PropertyDetailsGrid;
